import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ComicHeading, ComicPanel, ComicButton } from '../components/ComicPanel';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { 
  FileText, Settings, Users, Download, ChevronRight, 
  Plus, Edit, Trash2, Eye, EyeOff, Save, X, Loader2,
  ArrowLeft, ExternalLink
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin Layout Component
const AdminLayout = ({ children }) => {
  const location = useLocation();
  const { token } = useAuth();
  
  const navItems = [
    { to: '/admin', label: 'Content', icon: FileText, exact: true },
    { to: '/admin/settings', label: 'Site Settings', icon: Settings },
    { to: '/admin/members', label: 'Members', icon: Users },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <ComicPanel className="p-4">
              <ComicHeading as="h2" className="text-xl mb-4" data-testid="admin-title">
                Admin Panel
              </ComicHeading>
              <nav className="space-y-2">
                {navItems.map((item) => {
                  const isActive = item.exact 
                    ? location.pathname === item.to
                    : location.pathname.startsWith(item.to);
                  
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`flex items-center gap-3 px-4 py-3 font-bold uppercase tracking-wider text-sm transition-colors ${
                        isActive 
                          ? 'bg-yellow-500 text-black' 
                          : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                      }`}
                      data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </Link>
                  );
                })}
              </nav>
            </ComicPanel>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </Layout>
  );
};

// Content Management
const AdminContent = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const axiosAuth = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axiosAuth.get('/admin/content');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axiosAuth.delete(`/admin/content/${id}`);
      toast.success('Content deleted');
      fetchContent();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setShowEditor(true);
  };

  const handleSave = () => {
    setShowEditor(false);
    fetchContent();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (showEditor) {
    return (
      <AdminLayout>
        <ContentEditor 
          item={editingItem} 
          onSave={handleSave} 
          onCancel={() => setShowEditor(false)} 
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <ComicHeading as="h1" className="text-2xl" data-testid="admin-content-title">
          Content Management
        </ComicHeading>
        <ComicButton variant="secondary" onClick={handleCreate} data-testid="admin-create-content">
          <Plus className="w-4 h-4 mr-2" />
          New Content
        </ComicButton>
      </div>

      <ComicPanel className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-yellow-500">Title</TableHead>
              <TableHead className="text-yellow-500">Section</TableHead>
              <TableHead className="text-yellow-500">Status</TableHead>
              <TableHead className="text-yellow-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} className="border-slate-700">
                <TableCell className="font-bold text-white">{item.title}</TableCell>
                <TableCell className="capitalize text-slate-300">{item.section}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 text-xs font-bold uppercase ${
                    item.isPublished ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-300'
                  }`}>
                    {item.isPublished ? 'Published' : 'Draft'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleEdit(item)}
                      className="text-slate-400 hover:text-yellow-500"
                      data-testid={`edit-content-${item.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(item.id)}
                      className="text-slate-400 hover:text-red-500"
                      data-testid={`delete-content-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {item.isPublished && (
                      <Link 
                        to={`/read/${item.slug}`}
                        className="text-slate-400 hover:text-blue-500"
                        target="_blank"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-slate-400 py-8">
                  No content yet. Click "New Content" to create your first item.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ComicPanel>
    </AdminLayout>
  );
};

// Content Editor
const ContentEditor = ({ item, onSave, onCancel }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    title: item?.title || '',
    slug: item?.slug || '',
    section: item?.section || 'skits',
    imagePaths: item?.imagePaths?.join('\n') || '',
    caption: item?.caption || '',
    videoUrl: item?.videoUrl || '',
    videoType: item?.videoType || 'external',
    isPublished: item?.isPublished || false,
    isMembersOnly: item?.isMembersOnly ?? true,
    isFeatured: item?.isFeatured || false,
  });
  const [saving, setSaving] = useState(false);

  const axiosAuth = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSlugify = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...formData,
      imagePaths: formData.imagePaths.split('\n').filter(p => p.trim()),
    };

    try {
      if (item) {
        await axiosAuth.put(`/admin/content/${item.id}`, payload);
        toast.success('Content updated');
      } else {
        await axiosAuth.post('/admin/content', payload);
        toast.success('Content created');
      }
      onSave();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button 
        onClick={onCancel}
        className="flex items-center gap-2 text-slate-400 hover:text-yellow-500 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Content
      </button>

      <ComicHeading as="h1" className="text-2xl mb-6">
        {item ? 'Edit Content' : 'New Content'}
      </ComicHeading>

      <ComicPanel className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
                Title
              </Label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                onBlur={() => !formData.slug && handleSlugify()}
                required
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none"
                data-testid="content-title"
              />
            </div>

            <div>
              <Label className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
                Slug
              </Label>
              <div className="flex gap-2 mt-2">
                <Input
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  className="bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none"
                  data-testid="content-slug"
                />
                <ComicButton type="button" variant="ghost" onClick={handleSlugify}>
                  Auto
                </ComicButton>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
              Section
            </Label>
            <Select value={formData.section} onValueChange={(value) => setFormData(prev => ({ ...prev, section: value }))}>
              <SelectTrigger className="mt-2 bg-slate-900 border-2 border-slate-700 text-white rounded-none" data-testid="content-section">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skits">Skits</SelectItem>
                <SelectItem value="tob-skits">TOB Skits</SelectItem>
                <SelectItem value="sporc-minute">Sporc Minute</SelectItem>
                <SelectItem value="sporc-sparks">Sporc Sparks</SelectItem>
                <SelectItem value="piggee-pen-case-files">Piggee Pen Case Files</SelectItem>
                <SelectItem value="lore">Lore</SelectItem>
                <SelectItem value="bonus">Bonus</SelectItem>
                <SelectItem value="updates">Updates</SelectItem>
                <SelectItem value="interactive">Interactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
              Image Paths (one per line)
            </Label>
            <Textarea
              name="imagePaths"
              value={formData.imagePaths}
              onChange={handleChange}
              rows={5}
              className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none resize-none font-mono text-sm"
              placeholder="/images/skits/episode-1-page-1.jpg&#10;/images/skits/episode-1-page-2.jpg"
              data-testid="content-images"
            />
            <p className="text-slate-500 text-sm mt-2">
              Enter image URLs or paths. Local images should be in /public/images/
            </p>
          </div>

          <div>
            <Label className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
              Caption (optional)
            </Label>
            <Textarea
              name="caption"
              value={formData.caption}
              onChange={handleChange}
              rows={2}
              className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none resize-none"
              data-testid="content-caption"
            />
          </div>
<div className="grid md:grid-cols-2 gap-6">
  <div>
    <Label className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
      Video URL (optional)
    </Label>
    <Input
      name="videoUrl"
      value={formData.videoUrl}
      onChange={handleChange}
      className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none"
      placeholder="https://youtube.com/shorts/... or /videos/sporc-minute-1.mp4"
      data-testid="content-video-url"
    />
  </div>

  <div>
    <Label className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
      Video Type
    </Label>
    <Select
      value={formData.videoType}
      onValueChange={(value) => setFormData(prev => ({ ...prev, videoType: value }))}
    >
      <SelectTrigger className="mt-2 bg-slate-900 border-2 border-slate-700 text-white rounded-none" data-testid="content-video-type">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="external">External Link</SelectItem>
        <SelectItem value="mp4">MP4 File</SelectItem>
        <SelectItem value="youtube">YouTube</SelectItem>
        <SelectItem value="instagram">Instagram Reel</SelectItem>
      </SelectContent>
    </Select>
  </div>
</div>

          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <Switch
                id="isPublished"
                checked={formData.isPublished}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isPublished: checked }))}
                data-testid="content-published"
              />
              <Label htmlFor="isPublished" className="text-white">Published</Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="isMembersOnly"
                checked={formData.isMembersOnly}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isMembersOnly: checked }))}
                data-testid="content-members-only"
              />
              <Label htmlFor="isMembersOnly" className="text-white">Members Only</Label>
            </div>
          </div>
<div className="flex items-center gap-3">
  <Switch
    id="isFeatured"
    checked={formData.isFeatured}
    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isFeatured: checked }))}
    data-testid="content-featured"
  />
  <Label htmlFor="isFeatured" className="text-white">Featured</Label>
</div>
          <div className="flex justify-end gap-4 pt-4">
            <ComicButton type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </ComicButton>
            <ComicButton type="submit" variant="primary" disabled={saving} data-testid="content-save">
              {saving ? 'Saving...' : 'Save Content'}
            </ComicButton>
          </div>
        </form>
      </ComicPanel>
    </div>
  );
};

// Site Settings
const AdminSettings = () => {
  const { token } = useAuth();
  const { refreshSite } = useSite();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const axiosAuth = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axiosAuth.get('/admin/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const flags = settings.featureFlags;
      await axiosAuth.put('/admin/settings', {
        interactiveMode: flags.interactiveMode,
        amazonBuyLink: flags.amazonBuyLink,
        donationLink: flags.donationLink,
        interactiveCtaLabel: flags.interactiveCtaLabel,
      });
      toast.success('Settings saved');
      refreshSite();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateFlag = (key, value) => {
    setSettings(prev => ({
      ...prev,
      featureFlags: {
        ...prev.featureFlags,
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const flags = settings?.featureFlags || {};

  return (
    <AdminLayout>
      <ComicHeading as="h1" className="text-2xl mb-6" data-testid="admin-settings-title">
        Site Settings
      </ComicHeading>

      <div className="space-y-6">
        <ComicPanel className="p-6">
          <h2 className="text-yellow-500 uppercase font-bold tracking-widest text-lg mb-4">
            Interactive Mode
          </h2>
          <p className="text-slate-400 mb-4">
            Control whether the Interactive section is live or redirects to the Donate page.
          </p>
          <Select 
            value={flags.interactiveMode || 'donation_only'} 
            onValueChange={(value) => updateFlag('interactiveMode', value)}
          >
            <SelectTrigger className="bg-slate-900 border-2 border-slate-700 text-white rounded-none w-full max-w-xs" data-testid="settings-interactive-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="donation_only">Donation Only (Coming Soon)</SelectItem>
              <SelectItem value="interactive_live">Interactive Live</SelectItem>
            </SelectContent>
          </Select>
        </ComicPanel>

        <ComicPanel className="p-6">
          <h2 className="text-yellow-500 uppercase font-bold tracking-widest text-lg mb-4">
            External Links
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Amazon Buy Link</Label>
              <Input
                value={flags.amazonBuyLink || ''}
                onChange={(e) => updateFlag('amazonBuyLink', e.target.value)}
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none"
                placeholder="https://amazon.com/..."
                data-testid="settings-amazon-link"
              />
            </div>

            <div>
              <Label className="text-slate-300">Donation Link (optional)</Label>
              <Input
                value={flags.donationLink || ''}
                onChange={(e) => updateFlag('donationLink', e.target.value)}
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none"
                placeholder="https://ko-fi.com/... or https://patreon.com/..."
                data-testid="settings-donation-link"
              />
            </div>

            <div>
              <Label className="text-slate-300">Interactive CTA Label</Label>
              <Input
                value={flags.interactiveCtaLabel || ''}
                onChange={(e) => updateFlag('interactiveCtaLabel', e.target.value)}
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white focus:border-yellow-500 rounded-none"
                placeholder="Read Interactive"
                data-testid="settings-interactive-label"
              />
            </div>
          </div>
        </ComicPanel>

        <div className="flex justify-end">
          <ComicButton variant="primary" onClick={handleSave} disabled={saving} data-testid="settings-save">
            {saving ? 'Saving...' : 'Save Settings'}
          </ComicButton>
        </div>
      </div>
    </AdminLayout>
  );
};

// Members Management
const AdminMembers = () => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snippetDialog, setSnippetDialog] = useState(false);
  const [snippet, setSnippet] = useState(null);

  const axiosAuth = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axiosAuth.get('/admin/members');
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'banned' ? 'active' : 'banned';
    const action = newStatus === 'banned' ? 'ban' : 'unban';
    
    if (!window.confirm(`Are you sure you want to ${action} this member?`)) return;
    
    try {
      await axiosAuth.put('/admin/members/ban', { userId, status: newStatus });
      toast.success(`Member ${action}ned`);
      fetchMembers();
    } catch (error) {
      toast.error(`Failed to ${action} member`);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axiosAuth.get('/admin/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'members_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleGenerateSnippet = async () => {
    // Find a published content item
    try {
      const contentResponse = await axiosAuth.get('/admin/content');
      const publishedItem = contentResponse.data.find(item => item.isPublished);
      
      if (!publishedItem) {
        toast.error('No published content to generate snippet for');
        return;
      }

      const response = await axiosAuth.post(`/admin/email-snippet?title=${encodeURIComponent(publishedItem.title)}&slug=${encodeURIComponent(publishedItem.slug)}`);
      setSnippet(response.data);
      setSnippetDialog(true);
    } catch (error) {
      toast.error('Failed to generate snippet');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <ComicHeading as="h1" className="text-2xl" data-testid="admin-members-title">
          Members ({members.length})
        </ComicHeading>
        <div className="flex gap-4">
          <ComicButton variant="ghost" onClick={handleGenerateSnippet} data-testid="admin-generate-snippet">
            Email Snippet
          </ComicButton>
          <ComicButton variant="secondary" onClick={handleExportCSV} data-testid="admin-export-csv">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </ComicButton>
        </div>
      </div>

      <ComicPanel className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-yellow-500">Email</TableHead>
              <TableHead className="text-yellow-500">Verified</TableHead>
              <TableHead className="text-yellow-500">Role</TableHead>
              <TableHead className="text-yellow-500">Updates</TableHead>
              <TableHead className="text-yellow-500">Status</TableHead>
              <TableHead className="text-yellow-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.userId} className="border-slate-700">
                <TableCell className="text-white">{member.email}</TableCell>
                <TableCell>
                  {member.isVerified ? (
                    <span className="text-green-500">Yes</span>
                  ) : (
                    <span className="text-slate-500">No</span>
                  )}
                </TableCell>
                <TableCell className="capitalize text-slate-300">{member.role}</TableCell>
                <TableCell>
                  {member.wantsUpdates ? (
                    <span className="text-green-500">Opted in</span>
                  ) : (
                    <span className="text-slate-500">Opted out</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 text-xs font-bold uppercase ${
                    member.status === 'banned' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                  }`}>
                    {member.status}
                  </span>
                </TableCell>
                <TableCell>
                  {member.role !== 'owner' && (
                    <button 
                      onClick={() => handleBan(member.userId, member.status)}
                      className={`text-sm font-bold uppercase ${
                        member.status === 'banned' ? 'text-green-500 hover:text-green-400' : 'text-red-500 hover:text-red-400'
                      }`}
                      data-testid={`ban-member-${member.userId}`}
                    >
                      {member.status === 'banned' ? 'Unban' : 'Ban'}
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ComicPanel>

      {/* Email Snippet Dialog */}
      <Dialog open={snippetDialog} onOpenChange={setSnippetDialog}>
        <DialogContent className="bg-slate-800 border-2 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-yellow-500 uppercase font-bold tracking-widest">
              Email Snippet
            </DialogTitle>
          </DialogHeader>
          {snippet && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400">Subject</Label>
                <Input
                  value={snippet.subject}
                  readOnly
                  className="mt-1 bg-slate-900 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400">Body</Label>
                <Textarea
                  value={snippet.body}
                  readOnly
                  rows={5}
                  className="mt-1 bg-slate-900 border-slate-700 text-white resize-none"
                />
              </div>
              <div>
                <Label className="text-slate-400">Link</Label>
                <Input
                  value={snippet.link}
                  readOnly
                  className="mt-1 bg-slate-900 border-slate-700 text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <ComicButton variant="ghost" onClick={() => setSnippetDialog(false)}>
              Close
            </ComicButton>
            <ComicButton 
              variant="primary" 
              onClick={() => {
                navigator.clipboard.writeText(`Subject: ${snippet.subject}\n\n${snippet.body}`);
                toast.success('Copied to clipboard');
              }}
            >
              Copy to Clipboard
            </ComicButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export { AdminContent, AdminSettings, AdminMembers };
